import { PageLoader } from "@/components/ui/Spinner";
import { EmptyState, TableShell, Th } from "@/features/power_projects/components/ui";
import { useLeadFunnel, type FunnelRow } from "@/features/leads/api/leads";

/**
 * Lejek pozyskiwania na liczbach — „czy ten moduł zarabia i którą drogą".
 *
 * Dwie rzeczy, które ten ekran mówi wprost, bo inaczej wszyscy czytają je źle:
 *
 * 1. Liczymy FIRMY, nie maile. Firma, do której poszły trzy przypomnienia, jest
 *    jedną firmą w kolumnie „napisano".
 * 2. Kolejny etap NIE zawiera się w poprzednim: firma z targów wchodzi do systemu
 *    bez naszego maila, a odpisać może tylko ta, do której napisaliśmy. Dlatego
 *    odsetek odpowiedzi liczymy od „napisano", a konwersję od wszystkich firm.
 */

const PERCENT = (part: number, whole: number) =>
  whole > 0 ? `${Math.round((part / whole) * 100)}%` : "—";

function Tile({
  label,
  value,
  hint,
}: {
  label: string;
  value: number;
  hint?: string;
}) {
  return (
    <div className="card flex-1 p-3">
      <div className="section-label">{label}</div>
      <div className="font-mono text-2xl">{value}</div>
      {hint && (
        <div className="text-[11px] text-[var(--color-text-muted)]">{hint}</div>
      )}
    </div>
  );
}

export function LeadsFunnelPage() {
  const { data, isLoading } = useLeadFunnel();

  if (isLoading) return <PageLoader />;
  if (!data) return <EmptyState>Brak danych.</EmptyState>;

  const t: FunnelRow = data.total;
  const c = data.search_candidates;
  const d = data.delivery;

  return (
    <>
      <div className="mb-4 flex flex-wrap gap-3">
        <Tile label="Firmy w bazie" value={t.leads} />
        <Tile
          label="Napisano do"
          value={t.mailed}
          hint={`${PERCENT(t.mailed, t.leads)} bazy`}
        />
        <Tile
          label="Odpisało"
          value={t.replied}
          hint={`${PERCENT(t.replied, t.mailed)} tych, do których napisano`}
        />
        <Tile
          label="Odbicia"
          value={t.bounced}
          hint={`${PERCENT(t.bounced, t.mailed)} wysyłek — adres nie działa`}
        />
        <Tile
          label="Weszło do systemu"
          value={t.converted}
          hint={`${PERCENT(t.converted, t.leads)} bazy`}
        />
        <Tile label="Odmowa" value={t.rejected} hint="firma nie była zainteresowana" />
        <Tile
          label="Nie chcemy"
          value={t.declined}
          hint="nasza decyzja, nie ich odmowa"
        />
        <Tile label="Nie kontaktować" value={t.blocked} />
      </div>

      <p className="section-label mb-2">Skąd te firmy pochodzą</p>
      {data.by_source.length === 0 ? (
        <div className="card">
          <EmptyState>Nie ma jeszcze żadnej firmy w bazie.</EmptyState>
        </div>
      ) : (
        <TableShell>
          <thead>
            <tr>
              <Th>Źródło</Th>
              <Th className="text-right">Firmy</Th>
              <Th className="text-right">Napisano</Th>
              <Th className="text-right">Odpisało</Th>
              <Th className="text-right">Weszło do systemu</Th>
              <Th className="text-right">Odmowa</Th>
            </tr>
          </thead>
          <tbody>
            {data.by_source.map((row) => (
              <tr key={row.source}>
                <td className="px-3 py-2 text-sm">{row.label}</td>
                <td className="px-3 py-2 text-right font-mono text-xs">{row.leads}</td>
                <td className="px-3 py-2 text-right font-mono text-xs">{row.mailed}</td>
                <td className="px-3 py-2 text-right font-mono text-xs">
                  {row.replied}
                  <span className="ml-1.5 text-[var(--color-text-muted)]">
                    {PERCENT(row.replied, row.mailed)}
                  </span>
                </td>
                <td className="px-3 py-2 text-right font-mono text-xs">
                  {row.converted}
                  <span className="ml-1.5 text-[var(--color-text-muted)]">
                    {PERCENT(row.converted, row.leads)}
                  </span>
                </td>
                <td className="px-3 py-2 text-right font-mono text-xs">
                  {row.rejected}
                </td>
              </tr>
            ))}
          </tbody>
        </TableShell>
      )}

      <p className="section-label mt-5 mb-2">Dostarczalność wysłanych maili</p>
      <div className="flex flex-wrap gap-3">
        <Tile label="Wysłano" value={d.sent} />
        <Tile
          label="Potwierdzone doręczenie"
          value={d.delivered}
          hint={`${PERCENT(d.delivered, d.sent)} — to dolna granica`}
        />
        <Tile
          label="Odbicia trwałe"
          value={d.hard_bounced}
          hint="adres nie istnieje — wyłączony z wysyłki"
        />
        <Tile
          label="Odbicia chwilowe"
          value={d.soft_bounced}
          hint="skrzynka pełna, chwilowy błąd serwera"
        />
        <Tile label="Bez informacji" value={d.unknown} />
      </div>
      <p className="section-label mt-2">
        Potwierdzenie doręczenia odsyła tylko część serwerów — Gmail na przykład
        nie. „Bez informacji" nie znaczy „nie dotarło"; pewne jest wyłącznie to, co
        pokazują odbicia.
      </p>

      <p className="section-label mt-5 mb-2">Kandydaci z wyszukiwania AI</p>
      <div className="flex flex-wrap gap-3">
        <Tile label="Zaproponował model" value={c.proposed} />
        <Tile
          label="Przyjęto do bazy"
          value={c.accepted}
          hint={`${PERCENT(c.accepted, c.proposed)} propozycji`}
        />
        <Tile label="Odrzucono" value={c.rejected} />
      </div>

      <p className="section-label mt-4">
        Liczone są firmy, nie maile — trzy przypomnienia do jednej firmy to jedna
        firma w kolumnie „napisano". Etapy nie zawierają się w sobie: firma poznana
        na targach wchodzi do systemu bez naszego maila.
      </p>
    </>
  );
}
